import React from 'react';

export interface AudioOnlyOverlayProps {
  logoUrl: string;
  visible: boolean;
}

/**
 * Audio Only overlay — displayed when the viewer selects "Audio Only" quality.
 *
 * Shows the association logo centered on a dark background, replacing the video
 * viewport while audio continues playing (Req 1.36).
 */
export const AudioOnlyOverlay: React.FC<AudioOnlyOverlayProps> = ({ logoUrl, visible }) => {
  if (!visible) return null;

  return (
    <div
      style={styles.container}
      role="img"
      aria-label="Mode audio uniquement — logo de l'association"
    >
      <div style={styles.logoWrapper}>
        <img
          src={logoUrl}
          alt="Capoeira Sempre Senzala"
          style={styles.logo}
          onError={(e) => {
            // If logo fails to load, show a fallback radio icon
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
      <RadioWaveAnimation />
    </div>
  );
};

/**
 * Subtle animated radio wave indicator below the logo.
 */
const RadioWaveAnimation: React.FC = () => (
  <div style={styles.waveContainer} aria-hidden="true">
    <div style={{ ...styles.wave, animationDelay: '0s' }} />
    <div style={{ ...styles.wave, animationDelay: '0.3s' }} />
    <div style={{ ...styles.wave, animationDelay: '0.6s' }} />
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    zIndex: 5,
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '200px',
    height: '200px',
  },
  logo: {
    maxWidth: '160px',
    maxHeight: '160px',
    objectFit: 'contain',
    opacity: 0.95,
    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',
  },
  waveContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '24px',
  },
  wave: {
    width: '4px',
    height: '20px',
    backgroundColor: '#006B2B',
    borderRadius: '2px',
    animation: 'audioWave 1.2s ease-in-out infinite',
  },
};
